import { delay, isEqual } from "lodash-es";
import { ComponentPublicInstance, computed, Ref, ref, watch } from "vue";

import type { Controller, TaskId } from "@ctrl/controller-rpc";

import type ContextMenu from "@/components/context-menu/ContextMenu.vue";
import type ModalConsole from "@/components/ModalConsole.vue";
import {
  assignShortcuts,
  selectNextItem,
  selectPreviousItem,
  Shortcuts,
  startEditInlineItemTitle,
  stopEditInlineItemTitle,
  unSelectItem,
} from "@/components/shortcuts";
import { getHTMLElement } from "@/components/utils";
import { bindAsync, releasableRef } from "@/ref";
import { isNativeTaskId } from "@/utils/id";

export const useTaskList = (
  taskIds: Ref<TaskId[] | null | undefined>,
  inject: {
    controller: Controller;
    console: Ref<InstanceType<typeof ModalConsole>>;
    contextMenu: Ref<InstanceType<typeof ContextMenu>>;
    isInputFocused: Ref<boolean>;
    isModalShown: Ref<boolean>;
  },
  options?: {
    unselectItems?: () => void;
    isActivated?: Ref<boolean>;
    // Should be set to false if there are multiple task lists on the same view
    // For instance, in the Planner the task list in the sidebar is set to true but all task lists in the footer are set to false.
    isSelectable?: Ref<{ withUp: boolean; withDown: boolean }>;
    // This is used when having multiple lists chained.
    containerEl?: Ref<InstanceType<typeof HTMLElement> | undefined>;
  }
) => {
  const isActivated = computed(() => options?.isActivated?.value ?? true);
  const isSelectable = computed(
    () =>
      options?.isSelectable?.value ?? {
        withUp: isActivated.value,
        withDown: isActivated.value,
      }
  );
  const unselectItems = options?.unselectItems;
  const containerEl = options?.containerEl;
  const { controller, contextMenu, console } = inject;
  const isContextMenuHidden = computed<boolean>(
    () => !contextMenu.value?.isDisplayed
  );
  const taskListEl = ref<ComponentPublicInstance | HTMLElement>();
  const addTaskButtonEl = ref<{ focus?: () => void }>();
  const selectedTaskEl = ref<HTMLElement>();
  // TODO: the selection state is misdesigned — the source of truth should be
  // a selected task *id*, with the index derived on demand (for up/down
  // navigation). Storing the index forces every selection to resolve against
  // the current list, which breaks for ids that are not listed yet (see
  // `pendingSelectionId`) and makes the `taskIds` watcher below re-derive
  // the selection whenever the list changes.
  const selectedTaskIndex = ref<number | null>(null);
  const isConsoleOpened = computed<boolean>(() => console.value?.opened);
  const isEditing = ref(false);
  const isLoading = computed(
    () => taskIds.value === null || taskIds.value === undefined
  );
  const selectedTaskId = computed<TaskId | null>(() =>
    taskIds.value && selectedTaskIndex.value !== null
      ? taskIds.value[selectedTaskIndex.value]
      : null
  );
  const isInViewport = (
    el: HTMLElement,
    taskListEl: ComponentPublicInstance | HTMLElement
  ): boolean => {
    const elBoundaries = el.getBoundingClientRect();
    const containerBoundaries = containerEl?.value
      ? containerEl.value.getBoundingClientRect()
      : getHTMLElement(taskListEl).getBoundingClientRect();
    return (
      elBoundaries.top >= containerBoundaries.top &&
      elBoundaries.bottom <= containerBoundaries.bottom
    );
  };
  const hasTasks = computed<boolean>(
    () => !!(taskIds.value && taskIds.value.length > 0)
  );
  const startEdit = async () => {
    if (isEditing.value || !hasTasks.value || selectedTaskIndex.value === null)
      return;
    if (selectedTaskId.value && isNativeTaskId(selectedTaskId.value)) {
      const task = await bindAsync(selectedTaskId, async (id) =>
        id ? await controller.task.get(id) : releasableRef(null)
      );
      task.release();
      if (task.value?.canEditTitle) {
        isEditing.value = true;
      }
    } else {
      isEditing.value = true;
    }
  };
  const scrollToSelectedTask = () => {
    if (!selectedTaskEl.value || !taskListEl.value) return;
    if (!isInViewport(selectedTaskEl.value, taskListEl.value)) {
      if (!isSelectable.value.withUp && !isSelectable.value.withDown) {
        selectedTaskEl.value.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      } else if (selectedTaskIndex.value === 0) {
        const el = containerEl?.value
          ? containerEl.value
          : getHTMLElement(taskListEl.value);
        el.scrollTo({ behavior: "smooth", top: 0 });
        if (el.scrollTop !== 0) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "start",
          });
          delay(() => {
            el.scrollTop = 0;
          }, 300); // backup
        }
      } else if (selectedTaskIndex.value === lastIndex.value) {
        const el = containerEl?.value
          ? containerEl.value
          : getHTMLElement(taskListEl.value);
        const top = el.scrollHeight - 1; // hack
        el.scrollTo({ behavior: "smooth", top });
        if (el.scrollTop !== top) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "end",
          });
          delay(() => {
            el.scrollTop = top + 1;
          }, 300); // backup
        }
      } else {
        selectedTaskEl.value.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  };
  const selectUp = () => {
    if (
      !hasTasks.value ||
      selectedTaskIndex.value === null ||
      selectedTaskIndex.value === 0 ||
      !taskListEl.value ||
      !isContextMenuHidden.value
    )
      return;
    selectedTaskIndex.value--;
  };
  const selectDown = () => {
    if (
      !hasTasks.value ||
      selectedTaskIndex.value === null ||
      selectedTaskIndex.value === lastIndex.value ||
      !taskListEl.value ||
      !isContextMenuHidden.value
    )
      return;
    selectedTaskIndex.value++;
  };
  const setSelectedTaskEl = (el: HTMLElement) => {
    selectedTaskEl.value = el;
    scrollToSelectedTask();
  };
  const selectTaskByIndex = (index: number) => {
    if (
      isLoading.value ||
      !hasTasks.value ||
      selectedTaskIndex.value === index ||
      !isActivated.value
    )
      return;
    unselectItems?.();
    quitEditing();
    selectedTaskIndex.value = index;
  };
  // A selection requested while the task is not (yet) in the list — e.g. a
  // task just created through the add-task input, which only reaches the
  // list with the next live update. Applied by the `taskIds` watcher as soon
  // as the id shows up; cancelled by any unselect or other selection first.
  let pendingSelectionId: string | null = null;
  const selectTaskById = (taskId: string) => {
    if (!taskIds.value || !isActivated.value) return;
    const index = taskIds.value.findIndex((id) => id === taskId);
    if (index === -1) {
      pendingSelectionId = taskId;
      return;
    }
    pendingSelectionId = null;
    selectTaskByIndex(index);
  };
  const unselectTask = (e?: MouseEvent) => {
    quitEditing();
    // Hack: prevent vue3-clipboard library from removing the focus when copying a task.
    if (e && e.clientX === 0 && e.clientY === 0) return;
    pendingSelectionId = null;
    selectedTaskIndex.value = null;
    selectedTaskEl.value = undefined;
  };
  const quitEditing = () => {
    if (isEditing.value) isEditing.value = false;
  };
  const lastIndex = computed(() =>
    hasTasks.value ? taskIds.value!.length - 1 : 0
  );
  const stopWatchTaskIds = watch(
    taskIds,
    (newIds, oldIds) => {
      // A pending selection whose task has arrived (e.g. just created
      // through the add-task input) takes precedence.
      if (pendingSelectionId && newIds?.includes(pendingSelectionId)) {
        selectTaskById(pendingSelectionId);
        return;
      }
      const _index = selectedTaskIndex.value;
      // If nothing was selected, do nothing.
      if (_index === null) return;
      // Resolve the previously selected id against the *old* ids: the
      // `selectedTaskId` computed already reads the new list, so after a
      // reorder it would name whichever task moved into the old index and
      // the selection would stick to that position instead of following
      // the task.
      const _id =
        oldIds && _index < oldIds.length
          ? oldIds[_index]
          : selectedTaskId.value;
      // If nothing has changed, do nothing.
      if (isEqual(newIds, oldIds)) return;
      // If the list of tasks is empty, unselect the current task.
      if (!newIds || (newIds && newIds.length === 0)) {
        unselectTask();
        return;
      }
      // If something was selected, we must recompute the index to match the new position.
      if (_id !== null && !newIds!.includes(_id)) {
        // The task that was selected is no longer in the list: deselect it and seclect
        // another nearby task.
        unselectTask();
        if (_index >= 0 && _index < newIds!.length) {
          // Reselect the task at the same index.
          selectTaskByIndex(_index);
        } else if (_index === oldIds!.length - 1) {
          // Reselect the last task of the list.
          selectTaskByIndex(newIds!.length - 1);
        } else {
          // The index is out of bounds: do not reselect anything.
        }
      } else if (_id !== null) {
        // The task is still in the list: rebind the index to its (possibly
        // new) position. Set the index directly — going through
        // `selectTaskByIndex` would unselect other items and interrupt an
        // in-progress edit for what is only a position change.
        const newIndex = newIds!.indexOf(_id);
        if (newIndex !== -1 && newIndex !== _index) {
          selectedTaskIndex.value = newIndex;
        }
      }
    },
    { immediate: true }
  );
  const isTaskSelected = computed<boolean>(
    () => selectedTaskIndex.value !== null
  );
  const isShortcutsEnabled = computed<boolean>(
    () =>
      !isConsoleOpened.value &&
      hasTasks.value &&
      isActivated.value &&
      isContextMenuHidden.value
  );
  const injection = {
    isModalShown: inject.isModalShown,
    isInputFocused: inject.isInputFocused,
  };
  const shortcuts = computed<Shortcuts>(() =>
    isShortcutsEnabled.value
      ? {
          ...assignShortcuts(
            injection,
            startEditInlineItemTitle,
            () => {
              startEdit();
            },
            isTaskSelected
          ),
          ...assignShortcuts(
            injection,
            stopEditInlineItemTitle,
            quitEditing,
            isEditing
          ),
          ...assignShortcuts(
            injection,
            selectPreviousItem,
            selectUp,
            isTaskSelected
          ),
          ...assignShortcuts(
            injection,
            selectNextItem,
            selectDown,
            isTaskSelected
          ),
          ...assignShortcuts(
            injection,
            unSelectItem,
            () => unselectTask(),
            ref(isTaskSelected.value && !isEditing.value)
          ),
        }
      : {}
  );
  const release = () => {
    stopWatchTaskIds();
  };

  return {
    addTaskButtonEl,
    isEditing,
    isLoading,
    lastIndex,
    quitEditing,
    selectedTaskId,
    selectTaskById,
    setSelectedTaskEl,
    shortcuts,
    taskListEl,
    unselectTask,
    release,
  };
};
